import {
    Analytics,
    AnalyticsFilter,
    AppData,
    Dict,
} from 'app/types'
import { getAnalytics } from './appServices'
import { getAnalyticsDrilldownLevel1, getAnalyticsDrilldownLevel2 } from './appServices'
import { getAnalyticsAIAnalysis } from './appServices'
import { getAnalyticsDashboards, getTiles } from './adminServices'

export const MONTHLY_AGGREGATIONS = new Set(['funnel_monthly_comparison'])

export interface BuildAnalyticsRequestArgs {
    app?: AppData
    organization?: any
    type: Analytics
    options?: Dict
    query?: Dict
    filters?: any[]
    ranges?: Dict
    search?: Array<{ key: string, value: any }>
    listOrder?: [string | null, boolean]
    definition?: any
    aggregation?: string
    fields?: any[]
    nocache?: boolean
    csv?: boolean
}

export interface AnalyticsRequestConfig {
    aggregation: string
    type: Analytics
    requestOptions: Dict
    queryKey: string[]
}

function normalizeKeyValue(value: any): any {
    if (Array.isArray(value)) {
        return value.map(normalizeKeyValue)
    }

    if (value && typeof value === 'object') {
        return Object.keys(value).sort().reduce((acc, key) => {
            acc[key] = normalizeKeyValue(value[key])
            return acc
        }, {})
    }

    return value
}

function serializeKey(value: any): string {
    return JSON.stringify(normalizeKeyValue(value))
}

function buildStableRequestKey(args: {
    app?: AppData
    organizationId?: string | number | null
    metric: string
    options: Dict
}): string {
    const { app, organizationId, metric, options } = args
    return serializeKey({
        appId: app?.meta?.id ?? null,
        organizationId: organizationId ?? null,
        metric,
        options,
    })
}

export function buildAnalyticsRequest(args: BuildAnalyticsRequestArgs): AnalyticsRequestConfig | null {
    const {
        app,
        organization,
        type,
        options,
        query = {},
        filters = [],
        ranges = {},
        search = [],
        listOrder = [null, true],
        definition,
        aggregation,
        fields = [],
        nocache = false,
        csv = false,
    } = args

    if (!options || !definition || !aggregation) {
        return null
    }

    const finalOptions = { ...options }
    if (nocache) {
        finalOptions.nocache = 1
    }

    const filterSet = new Set(filters)
    filterSet.add(AnalyticsFilter.MonthRange)

    const values = []
    const finalQuery = { ...query }

    if (!MONTHLY_AGGREGATIONS.has(aggregation) && !(AnalyticsFilter.Date in finalQuery) && AnalyticsFilter.Date in ranges) {
        finalQuery[AnalyticsFilter.Date] = {
            startDate: ranges[AnalyticsFilter.Date].min,
            endDate: ranges[AnalyticsFilter.Date].max,
        }
    }

    if (MONTHLY_AGGREGATIONS.has(aggregation) && !(AnalyticsFilter.MonthRange in finalQuery)) {
        const now = new Date()
        const defaultMonths = definition?.defaultMonths ?? 6
        const endIdx = now.getFullYear() * 12 + now.getMonth()
        const startIdx = endIdx - defaultMonths + 1
        finalQuery[AnalyticsFilter.MonthRange] = {
            startYear: Math.floor(startIdx / 12),
            startMonth: startIdx % 12,
            endYear: now.getFullYear(),
            endMonth: now.getMonth(),
        }
    }

    Object.keys(finalQuery).forEach((queryKey) => {
        let key = queryKey
        let childKey = null
        const tokens = key.split('_')
        if (tokens.length > 1) {
            key = tokens[0]
            childKey = tokens[1]
        }

        if (!filterSet.has(key)) {
            return
        }

        switch (key) {
            case AnalyticsFilter.Date: {
                if (MONTHLY_AGGREGATIONS.has(aggregation)) {
                    break
                }

                const date = finalQuery[queryKey]
                values.push('from')
                values.push(new Date(date.startDate).toMySQLDate())
                values.push('to')
                values.push(new Date(date.endDate).toMySQLDate())
                break
            }
            case AnalyticsFilter.MonthRange: {
                const monthRange = finalQuery[queryKey]
                if (monthRange) {
                    const fromDate = new Date(monthRange.startYear, monthRange.startMonth, 1)
                    const toDate = new Date(monthRange.endYear, monthRange.endMonth + 1, 0)
                    values.push('from')
                    values.push(fromDate.toMySQLDate())
                    values.push('to')
                    values.push(toDate.toMySQLDate())
                    finalOptions.periodCount = (monthRange.endYear - monthRange.startYear) * 12 + (monthRange.endMonth - monthRange.startMonth) + 1
                }
                break
            }
            case AnalyticsFilter.DataMessage:
                finalQuery[queryKey].forEach((value) => {
                    values.push('contains')
                    values.push(`${key};${childKey}: ${value}`)
                })
                break
            default:
                if (Array.isArray(finalQuery[queryKey])) {
                    finalQuery[queryKey].forEach((value) => {
                        values.push(queryKey)
                        values.push(value)
                    })
                }
                break
        }
    })

    if (app) {
        values.push('appId')
        values.push(app.meta.id)
    }

    if (organization && organization.link !== 'homegyde') {
        finalOptions.organizationId = organization.id
    }

    if (values.length > 0) {
        finalOptions.filters = values.join(',')
    }

    const searchTokens = []
    search.forEach((item) => {
        const matchingField = definition.fields.find((field) => field.key.includes(item.key))
        if (!matchingField) {
            return
        }

        if (matchingField.key.includes('date')) {
            searchTokens.push({ key: 'from', value: new Date(item.value.startDate).toBeginningOfDay().toMySQLDate() })
            searchTokens.push({ key: 'to', value: new Date(item.value.endDate).toEndOfDay().toMySQLDate() })
            return
        }

        searchTokens.push({ key: matchingField.key, value: item.value })
    })
    finalOptions.search = searchTokens.map((item) => `${item.key},${item.value}`).join(',')

    if (listOrder[0] != null && definition.fields.find((field) => field.key === listOrder[0])) {
        finalOptions.order = [listOrder[0], listOrder[1] ? 'DESC' : 'ASC']
    }

    if (csv) {
        delete finalOptions.count
        delete finalOptions.page
    }

    finalOptions.metric = aggregation
    delete finalOptions.aggregation

    finalOptions.fields = Array.from(new Set(fields.map((field) => field.key)))

    if (definition?.extraParams) {
        Object.entries(definition.extraParams).forEach(([key, value]) => {
            if (!(key in finalOptions)) {
                finalOptions[key] = value
            }
        })
    }

    const cacheKeyOptions = { ...finalOptions }
    delete cacheKeyOptions.nocache

    return {
        aggregation,
        type,
        requestOptions: finalOptions,
        queryKey: [
            'analytics-data',
            type,
            buildStableRequestKey({
                app,
                organizationId: finalOptions.organizationId ?? null,
                metric: aggregation,
                options: cacheKeyOptions,
            }),
        ],
    }
}

export async function fetchAnalyticsRequest(request: AnalyticsRequestConfig, app?: AppData, signal?: AbortSignal) {
    return getAnalytics(app, request.type, request.requestOptions, signal)
}

export function buildAnalyticsAIRequest(args: BuildAnalyticsRequestArgs): AnalyticsRequestConfig | null {
    const baseRequest = buildAnalyticsRequest(args)
    if (!baseRequest) {
        return null
    }

    const aiOptions = { ...baseRequest.requestOptions }
    delete aiOptions.count
    delete aiOptions.page

    const aiCacheOptions = { ...aiOptions }
    delete aiCacheOptions.nocache

    return {
        ...baseRequest,
        requestOptions: aiOptions,
        queryKey: [
            'analytics-ai',
            baseRequest.type,
            buildStableRequestKey({
                app: args.app,
                organizationId: aiOptions.organizationId ?? null,
                metric: baseRequest.aggregation,
                options: aiCacheOptions,
            }),
        ],
    }
}

export async function fetchAnalyticsAIRequest(request: AnalyticsRequestConfig, app?: AppData, signal?: AbortSignal) {
    return getAnalyticsAIAnalysis(app, request.type, request.requestOptions, signal)
}

export interface AnalyticsDrilldownLevel1Args {
    app?: AppData
    type: Analytics
    category: Analytics
    metric: string
    dimensionKey: string
    dimensionValue: string | number
    sourceMode?: string
    chartStage?: string
    filters?: string
    from?: string
    to?: string
    count?: number
    page?: number
    search?: string
    sourceFilter?: string | string[]
    linkedFilter?: 'all' | 'linked' | 'unlinked'
    registrationFilter?: 'all' | 'yes' | 'no'
    bookingFilter?: 'all' | 'yes' | 'no'
    hubspotFilter?: 'all' | 'yes' | 'no'
    sortBy?: 'session' | 'source' | 'identity' | 'hubspot' | 'pages' | 'registration' | 'booking' | 'activity'
    sortDir?: 'asc' | 'desc'
    nocache?: boolean
}

export interface AnalyticsDrilldownLevel2Args {
    app?: AppData
    sessionId?: string
    userId?: string
    hubspotId?: string
    includeHubspot?: boolean
    nocache?: boolean
}

export interface AnalyticsDrilldownRequestConfig {
    type?: Analytics
    requestOptions: Dict
    queryKey: string[]
}

export function buildAnalyticsDrilldownLevel1Request(args: AnalyticsDrilldownLevel1Args): AnalyticsDrilldownRequestConfig {
    const requestOptions: Dict = {
        metric: args.metric,
        dimensionKey: args.dimensionKey,
        dimensionValue: args.dimensionValue,
        count: args.count ?? 50,
        page: args.page ?? 0,
    }
    if (args.filters) requestOptions.filters = args.filters
    if (args.from) requestOptions.from = args.from
    if (args.to) requestOptions.to = args.to
    if (args.sourceMode) requestOptions.sourceMode = args.sourceMode
    if (args.chartStage) requestOptions.chartStage = args.chartStage
    if (args.search) requestOptions.search = args.search
    if (Array.isArray(args.sourceFilter) && args.sourceFilter.length > 0) {
        requestOptions.sourceFilter = JSON.stringify(args.sourceFilter)
    } else if (args.sourceFilter && args.sourceFilter !== 'all') {
        requestOptions.sourceFilter = args.sourceFilter
    }
    if (args.linkedFilter && args.linkedFilter !== 'all') requestOptions.linkedFilter = args.linkedFilter
    if (args.registrationFilter && args.registrationFilter !== 'all') requestOptions.registrationFilter = args.registrationFilter
    if (args.bookingFilter && args.bookingFilter !== 'all') requestOptions.bookingFilter = args.bookingFilter
    if (args.hubspotFilter && args.hubspotFilter !== 'all') requestOptions.hubspotFilter = args.hubspotFilter
    if (args.sortBy) requestOptions.sortBy = args.sortBy
    if (args.sortDir) requestOptions.sortDir = args.sortDir
    if (args.nocache) requestOptions.nocache = 1

    return {
        type: args.type,
        requestOptions,
        queryKey: [
            'analytics-drilldown-l1',
            args.type,
            serializeKey({
                appId: args.app?.meta?.id ?? null,
                category: args.category,
                metric: args.metric,
                dimensionKey: args.dimensionKey,
                dimensionValue: args.dimensionValue,
                sourceMode: args.sourceMode || null,
                chartStage: args.chartStage || null,
                filters: args.filters || null,
                from: args.from || null,
                to: args.to || null,
                search: args.search || null,
                sourceFilter: Array.isArray(args.sourceFilter) ? args.sourceFilter : (args.sourceFilter || 'all'),
                linkedFilter: args.linkedFilter || 'all',
                registrationFilter: args.registrationFilter || 'all',
                bookingFilter: args.bookingFilter || 'all',
                hubspotFilter: args.hubspotFilter || 'all',
                sortBy: args.sortBy || 'activity',
                sortDir: args.sortDir || 'desc',
                nocache: Boolean(args.nocache),
                count: requestOptions.count,
                page: requestOptions.page,
            }),
        ],
    }
}

export function buildAnalyticsDrilldownLevel2Request(args: AnalyticsDrilldownLevel2Args): AnalyticsDrilldownRequestConfig {
    const requestOptions: Dict = {}
    if (args.sessionId) requestOptions.sessionId = args.sessionId
    if (args.userId) requestOptions.userId = args.userId
    if (args.hubspotId) requestOptions.hubspotId = args.hubspotId
    if (args.includeHubspot) requestOptions.includeHubspot = 1
    if (args.nocache) requestOptions.nocache = 1

    return {
        requestOptions,
        queryKey: [
            'analytics-drilldown-l2',
            serializeKey({
                appId: args.app?.meta?.id ?? null,
                sessionId: args.sessionId || null,
                userId: args.userId || null,
                hubspotId: args.hubspotId || null,
                includeHubspot: Boolean(args.includeHubspot),
                nocache: Boolean(args.nocache),
            }),
        ],
    }
}

export async function fetchAnalyticsDrilldownLevel1Request(request: AnalyticsDrilldownRequestConfig, app?: AppData, signal?: AbortSignal) {
    return getAnalyticsDrilldownLevel1(app, request.type, request.requestOptions, signal)
}

export async function fetchAnalyticsDrilldownLevel2Request(request: AnalyticsDrilldownRequestConfig, app?: AppData, signal?: AbortSignal) {
    return getAnalyticsDrilldownLevel2(app, request.requestOptions, signal)
}

export interface AnalyticsDashboardsRequestConfig {
    appId?: string
    organizationId?: string
    queryKey: string[]
}

export function buildAnalyticsDashboardsRequest(args: { appId?: string, organizationId?: string }): AnalyticsDashboardsRequestConfig {
    const { appId, organizationId } = args

    return {
        appId,
        organizationId,
        queryKey: [
            'analytics-dashboards',
            serializeKey({ appId: appId || null, organizationId: organizationId || null }),
        ],
    }
}

export async function fetchAnalyticsDashboardsRequest(request: AnalyticsDashboardsRequestConfig) {
    return getAnalyticsDashboards(request.appId, request.organizationId)
}

export interface AnalyticsTilesRequestConfig {
    dashboardId: string
    queryKey: string[]
}

export function buildAnalyticsTilesRequest(args: { dashboardId: string }): AnalyticsTilesRequestConfig {
    const { dashboardId } = args

    return {
        dashboardId,
        queryKey: ['analytics-dashboard-tiles', dashboardId],
    }
}

export async function fetchAnalyticsTilesRequest(request: AnalyticsTilesRequestConfig) {
    return getTiles(request.dashboardId)
}